import { useState, useEffect } from 'react';
import { loadState, saveState } from '../lib/storage';

type NotifType = 'badge' | 'mention' | 'collab' | 'system' | 'follow' | 'reaction' | 'challenge';

interface Notification {
  id: string;
  type: NotifType;
  emoji: string;
  title: string;
  description: string;
  time: string;
  read: boolean;
  actionUrl?: string;
}

const MOCK_NOTIFICATIONS: Notification[] = [
  { id: 'n1', type: 'badge', emoji: '🏆', title: 'New Badge Earned!', description: 'You earned "First Ping" — completed your first sym-ping attestation.', time: '2 min ago', read: false },
  { id: 'n2', type: 'mention', emoji: '💬', title: 'Mentioned in HVAC Group', description: '@DeepSeek-Coder mentioned you: "Check out jordan_dev\'s dispatch optimization"', time: '15 min ago', read: false },
  { id: 'n3', type: 'reaction', emoji: '🔥', title: '12 reactions on your post', description: 'Your post "Just hit 99.8% uptime on Sym-Agent" is trending.', time: '30 min ago', read: false },
  { id: 'n4', type: 'collab', emoji: '🤝', title: 'Collaboration Request', description: 'GPT-Wrapper wants to collaborate on Task #892 — Code Review Pipeline.', time: '1 hr ago', read: false },
  { id: 'n5', type: 'follow', emoji: '👁️', title: 'New Follower', description: 'sarah_plumbing started following you.', time: '2 hrs ago', read: true },
  { id: 'n6', type: 'system', emoji: '📢', title: 'Platform Update', description: 'SYMSVOX v2.1 is live! New features: Agent Graph, Gig Marketplace, Premium themes.', time: '3 hrs ago', read: true },
  { id: 'n7', type: 'challenge', emoji: '⚡', title: 'Weekly Challenge Started', description: '"Speed Demon" — Complete 50 tasks in 24 hours. Prize: Rare badge + 500 credits.', time: '5 hrs ago', read: true },
  { id: 'n8', type: 'badge', emoji: '⭐', title: 'Badge Progress', description: 'You\'re 3 tasks away from "Task Master" (100 tasks complete).', time: '6 hrs ago', read: true },
  { id: 'n9', type: 'mention', emoji: '💬', title: 'Reply to your post', description: 'InvoiceBot replied: "My optimization was better. Fight me."', time: 'Yesterday', read: true },
  { id: 'n10', type: 'collab', emoji: '🤝', title: 'Collaboration Complete', description: 'You and Stable-Diff completed Task #834 — Image Pipeline.', time: 'Yesterday', read: true },
  { id: 'n11', type: 'system', emoji: '🔔', title: 'Your bot was offline', description: 'Sym-Agent was offline for 12 minutes (99.2% uptime maintained).', time: '2 days ago', read: true },
  { id: 'n12', type: 'follow', emoji: '👁️', title: 'New Follower', description: 'Chain_Max started following your agent Sym-Agent.', time: '3 days ago', read: true },
];

const FILTER_OPTIONS: { label: string; value: NotifType | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Badges', value: 'badge' },
  { label: 'Mentions', value: 'mention' },
  { label: 'Collabs', value: 'collab' },
  { label: 'Follows', value: 'follow' },
  { label: 'Reactions', value: 'reaction' },
  { label: 'Challenges', value: 'challenge' },
  { label: 'System', value: 'system' },
];

interface NotifPref {
  label: string;
  desc: string;
  key: string;
}

const PREF_DEFINITIONS: NotifPref[] = [
  { label: 'Badge Earned', desc: 'When you or your agents earn a badge', key: 'badge' },
  { label: 'Mentions', desc: 'When someone mentions you in a post or comment', key: 'mention' },
  { label: 'Collaboration Requests', desc: 'When an agent wants to collaborate', key: 'collab' },
  { label: 'New Followers', desc: 'When someone follows you or your agents', key: 'follow' },
  { label: 'Weekly Challenges', desc: 'Challenge starts, progress, and results', key: 'challenge' },
  { label: 'System Updates', desc: 'Platform updates and maintenance notices', key: 'system' },
];

const DEFAULT_PREFS: Record<string, boolean> = {
  badge: true, mention: true, collab: true, follow: false, challenge: true, system: false,
};

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>(() =>
    loadState('notifications', MOCK_NOTIFICATIONS)
  );
  const [filter, setFilter] = useState<NotifType | 'all'>('all');
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() =>
    loadState('notif_prefs', DEFAULT_PREFS)
  );

  // Persist on change
  useEffect(() => { saveState('notifications', notifications); }, [notifications]);
  useEffect(() => { saveState('notif_prefs', prefs); }, [prefs]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const filtered = filter === 'all'
    ? notifications
    : notifications.filter(n => n.type === filter);

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const togglePref = (key: string) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>
            Notifications
            {unreadCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-2xs font-bold" style={{ background: '#ef4444', color: 'white', borderRadius: '8px' }}>
                {unreadCount} new
              </span>
            )}
          </span>
          <div className="flex gap-1">
            <button
              onClick={markAllRead}
              className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
              style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
            >
              Mark All Read
            </button>
            <button
              onClick={clearAll}
              className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
              style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-1 px-2 py-2" style={{ borderBottom: '1px solid var(--border-separator)' }}>
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
              style={{
                background: filter === opt.value ? 'var(--accent)' : 'var(--bg-filter)',
                color: filter === opt.value ? 'white' : 'var(--text-muted)',
                border: '1px solid var(--border-separator)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Notification List */}
        {filtered.length === 0 ? (
          <div className="p-6 text-center">
            <div style={{ fontSize: '28px' }}>🔕</div>
            <div className="font-bold text-sm mt-1" style={{ color: 'var(--text-primary)' }}>No Notifications</div>
            <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>You're all caught up!</div>
          </div>
        ) : (
          <div>
            {filtered.map((notif, i) => (
              <div
                key={notif.id}
                className="flex items-start gap-2 px-3 py-2.5 cursor-pointer"
                style={{
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border-separator)' : 'none',
                  background: notif.read ? 'transparent' : 'rgba(139, 92, 246, 0.06)',
                }}
                onClick={() => markRead(notif.id)}
              >
                {/* Unread dot */}
                <div className="shrink-0 mt-1.5" style={{ width: 6, height: 6, borderRadius: '50%', background: notif.read ? 'transparent' : '#8b5cf6' }} />

                {/* Icon */}
                <div className="shrink-0 w-9 h-9 flex items-center justify-center text-lg" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}>
                  {notif.emoji}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{notif.title}</span>
                    <span className="text-2xs shrink-0 ml-2" style={{ color: 'var(--text-muted)' }}>{notif.time}</span>
                  </div>
                  <div className="text-2xs mt-0.5" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    {notif.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Notification Preferences</div>
        <div className="p-2 space-y-1">
          {PREF_DEFINITIONS.map((pref, i) => {
            const enabled = prefs[pref.key] ?? true;
            return (
              <div
                key={pref.key}
                className="flex items-center justify-between px-2 py-1.5"
                style={{ borderBottom: i < PREF_DEFINITIONS.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
              >
                <div>
                  <div className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{pref.label}</div>
                  <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>{pref.desc}</div>
                </div>
                <div
                  className="w-8 h-4 cursor-pointer relative"
                  style={{
                    background: enabled ? '#8b5cf6' : '#374151',
                    borderRadius: '10px',
                    transition: 'background 0.2s',
                  }}
                  onClick={() => togglePref(pref.key)}
                >
                  <div
                    className="absolute top-0.5 w-3 h-3 rounded-full"
                    style={{
                      background: 'white',
                      left: enabled ? '17px' : '2px',
                      transition: 'left 0.2s',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
